import { useEffect, useState, useMemo } from "react";
import { createContext, useContext } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { getUserRights, getLockData, isYearEndProcessAdd } from '@/redux/actions';
import { selectAuth, selectUserRight, selectLockData } from '@/redux/selector';
import socket from '@/socket/socket';
import { MODULE_GROUPS, MODULE_IDS } from './moduleConstants';
import Layout from '@/components';
import dayjs from '@/utils/dayjs';

export const PermissionContext = createContext([]);

export const usePermissions = () => useContext(PermissionContext);

const AppContent = () => {
  // const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, customer, company, token, superUser, finyear } = useSelector(selectAuth, shallowEqual);
  const permissions = useSelector(selectUserRight, shallowEqual);
  const lockData = useSelector(selectLockData, shallowEqual);
  const [lockTrans, setLockTrans] = useState(false);
  const dispatch = useDispatch();

  // useEffect(() => {
  //   if (user?.id) {
  //     setLoading(true);
  //     dispatch(getUserRights()).then((res) => {
  //       setPermissions(res);
  //       setLoading(false);
  //     }).catch((e) => {
  //       setPermissions([]);
  //       setLoading(false);
  //     });
  //   } else {
  //     setLoading(false);
  //   }
  // }, [JSON.stringify(user?.id)]);

    useEffect(() => {
      if (!customer?.id) return;
      
      const joinCustomerRoom = () => {
          console.log('Socket connected:', socket.id);

          console.log('Joining customer:', customer?.id);

          socket.emit('join-customer', { customerId: customer?.id });
      };

      const handleDisconnect = (reason) => {
          console.log(
              'Socket disconnected:',
              reason
          );
      };

      const handleConnectError = (error) => {
          console.error(
              'Socket connection error:',
              error.message
          );
      };

      socket.on('connect', joinCustomerRoom);
      socket.on('disconnect', handleDisconnect);
      socket.on('connect_error', handleConnectError);

      if (!socket.connected) {
          socket.connect();
      } else {
          joinCustomerRoom();
      }

      return () => {
          socket.off('connect', joinCustomerRoom);
          socket.off('disconnect', handleDisconnect);
          socket.off('connect_error', handleConnectError);
      };
  }, [customer?.id]);

  useEffect(() => {
    if (user?.id) {
      setLoading(true);
      dispatch(getUserRights()).then((res) => {
        setLoading(false);
      }).catch((e) => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [JSON.stringify(user?.id)]);

  useEffect(() => {
    try {
      if (token && customer?.id) {
        dispatch(getLockData());
      }
    } catch (err) {
      console.error('Lock data failed ', err);
    }
  }, [customer?.id, company?.id, token]);

   useEffect(() => {
     if (token && !superUser?.id) {
      dispatch(isYearEndProcessAdd()).then((res) => {
        setLockTrans(res);
      }).catch((err) => {
        console.error('Get Year End Process failed ', err);
        setLockTrans(false);
      });
    }
  }, [customer?.id, company?.id, token, superUser?.id, finyear?.id]);

  const isValidDate = (value) => {
    const date = value && dayjs(value);
    if (!date?.isValid()) {
      return false;
    }
    const year = date.year()?.toString();
    if (year?.length < 4) {
        return false;
    }
    return true;
  };
  //get transaction or inventory lock value by date
  const getModuleLockByDate = (moduleId, date) => {
    if (!isValidDate(date)) {
      return false;
    }
    let lockDate;
    if (MODULE_GROUPS.INVENTORY.includes(moduleId)) {
      if (lockData?.Inventory_Date) {
        lockDate = dayjs(lockData?.Inventory_Date).startOf('day');
      } else {
        return false;
      }
    } else if (MODULE_GROUPS.TRANSACTIONS.includes(moduleId)) {
      const exclude = [MODULE_IDS.SALES_ORDER, MODULE_IDS.PURCHASE_ORDER, MODULE_IDS.PURCHASE_CHALLAN, MODULE_IDS.PURCHASE_CHALLAN_RETURN];
      if (lockTrans && !exclude.includes(moduleId)) {
        return true;
      } else {
        if (lockData?.Transaction_Date) {
          lockDate = dayjs(lockData?.Transaction_Date).startOf('day');
        } else {
          return false;
        }
      }
    } else {
      return false;
    }
    const day = dayjs(date).startOf('day');
    const isLock = day.isBefore(lockDate) || lockDate.isSame(day);
    return isLock;
  }

  // Optimized permission checking functions using useMemo
  const permissionUtils = useMemo(() => {
    // Create a map for faster permission lookup
    const permissionMap = new Map();
    permissions.forEach(permission => {
      permissionMap.set(permission?.Module?.Name, permission);
    });

    // Get permission by module ID
    const getModulePermission = (moduleId) => {
      return permissionMap.get(moduleId) || null;
    };

    // Get permission by module name
    const getModulePermissionByName = (moduleName) => {
      return permissions.find(permission => 
        permission.Module_Name === moduleName || 
        permission.Module_Label === moduleName
      ) || null;
    };

    // Check if user has specific permission for a module
    const hasPermission = (moduleId, permissionType) => {
      const permission = getModulePermission(moduleId);
      let lock = false;
      const exclude = [MODULE_IDS.SALES_ORDER, MODULE_IDS.PURCHASE_ORDER, MODULE_IDS.PURCHASE_CHALLAN, MODULE_IDS.PURCHASE_CHALLAN_RETURN];
      if (MODULE_GROUPS.TRANSACTIONS.includes(moduleId) && !exclude.includes(moduleId)) {
        lock = lockTrans;
      }
      if (!permission) return false;
      
      // Check if module is visible first
      if (!permission.Visible) return false;
      
      switch (permissionType) {
        case 'visible':
        case 'Visible':
          return permission.Visible === true;
        case 'view':
        case 'View':
          return permission.View === true;
        case 'add':
        case 'Add':
        case 'create':
        case 'Create':
          return !lock && permission.Add === true;
        case 'edit':
        case 'Edit':
        case 'update':
        case 'Update':
          return !lock && permission.Edit === true;
        case 'delete':
        case 'Delete':
          return !lock && permission.Delete === true;
        case 'print':
        case 'Print':
          return permission.Print === true;
        case 'export':
        case 'Export':
          return permission.Export === true;
        case 'same_date_update':
        case 'Same_Date_Update':
          return !lock && permission.Same_Date_Update === true;
        default:
          return false;
      }
    };

    // Check multiple permissions at once
    const hasPermissions = (moduleId, permissionTypes) => {
      return permissionTypes.every(type => hasPermission(moduleId, type));
    };

    // Get all permissions for a module
    const getModulePermissions = (moduleId) => {
      const permission = getModulePermission(moduleId);

      if (!permission) return null;
      
      return {
        visible: permission.Visible || false,
        view: permission.View || false,
        add: (permission.Add) || false,
        edit: (permission.Edit) || false,
        delete: (permission.Delete) || false,
        print: (permission.Print) || false,
        export: (permission.Export) || false,
        sameDateUpdate: (permission.Same_Date_Update) || false,
        // Helper computed properties
        canVisible: permission.Visible,
        canView: permission.Visible && permission.View,
        canAdd: permission.Visible && permission.Add,
        canEdit: permission.Visible && permission.Edit,
        canDelete: permission.Visible && permission.Delete,
        canPrint: permission.Visible && permission.Print,
        canExport: permission.Visible && permission.Export,
        canSameDateUpdate: permission.Visible && permission.Same_Date_Update,
        // Full CRUD permissions
        canCreate: permission.Visible && permission.Add,
        canRead: permission.Visible && permission.View,
        canUpdate: permission.Visible && permission.Edit,
        canDestroy: permission.Visible && permission.Delete,
      };
    };

    // Check if user has any permission for a module
    const hasAnyPermission = (moduleId) => {
      const permission = getModulePermission(moduleId);
      return permission && permission.Visible;
    };

    // Get all modules user has access to
    const getAccessibleModules = () => {
      return permissions.filter(permission => permission.Visible);
    };

    // Get modules with specific permission
    const getModulesWithPermission = (permissionType) => {
      return permissions.filter(permission => 
        permission.Visible && hasPermission(permission.Module_Id, permissionType)
      );
    };

    return {
      permissions,
      permissionMap,
      getModulePermission,
      getModulePermissionByName,
      hasPermission,
      hasPermissions,
      getModulePermissions,
      hasAnyPermission,
      getAccessibleModules,
      getModulesWithPermission,
      // Convenience methods for common operations
      canVisible: (moduleId) => hasPermission(moduleId, 'visible'),
      canView: (moduleId) => hasPermission(moduleId, 'view'),
      canAdd: (moduleId) => hasPermission(moduleId, 'add'),
      canEdit: (moduleId) => hasPermission(moduleId, 'edit'),
      canDelete: (moduleId) => hasPermission(moduleId, 'delete'),
      canPrint: (moduleId) => hasPermission(moduleId, 'print'),
      canExport: (moduleId) => hasPermission(moduleId, 'export'),
      canSameDateUpdate: (moduleId) => hasPermission(moduleId, 'same_date_update'),
      // lockData: lockData,
      getModuleLockByDate: (moduleId, date) => getModuleLockByDate(moduleId, date)
    };
  }, [permissions, lockData, lockTrans]);

  if (loading) return <div>Loading permissions...</div>;

  return (
    <PermissionContext.Provider value={permissionUtils}>
      <Layout/>
    </PermissionContext.Provider>
  );
};

// Export all permission utilities for easy importing
export * from './usePermissionHook';
export * from './withPermissions';
export * from './moduleConstants';

export default AppContent;